/*  Iceberg  */
import { FormSelect } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { ExpandMore } from '@mui/icons-material';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/*  Shared  */
import { FontSizes } from 'application/Shared/Constants';
import Helpers from 'application/Shared/Helpers/Functions';

/*  React  */
import React, { useEffect, useState } from 'react';

const handleValueChange = ({
    event,
    multiple,
    objectKey,
    onChange,
    setObjectSelection,
    value
}) => {
    if (event.target.value === '') {
        return;
    }

    let vals = value;
    if (
        typeof vals === 'object' &&
        vals !== null &&
        typeof vals[objectKey] !== 'undefined'
    ) {
        vals = vals[objectKey];
    }

    // Ignore Elements with 'noVal' as id (such as checkboxes within the select)
    if (Array.isArray(vals)) {
        vals = vals.filter((v) => {
            // NOTE: Not sure if this line is needed
            if (!Helpers.isset(v, [objectKey])) {
                return true;
            }

            if (v[objectKey] === 'noVal') {
                return false;
            }

            if (v[objectKey] === 'freeInput') {
                return true;
            }

            return true;
        });
    }

    if (multiple) {
        if (vals.length === 0) {
            vals = ['0'];
            setObjectSelection(vals);
            onChange(['0']);
            return;
        }

        if (vals.length > 1 && vals.includes('0')) {
            vals.splice(vals.indexOf('0'), 1);
        }

        setObjectSelection(vals);
    } else if (vals !== null) {
        setObjectSelection([vals]);
    } else {
        setObjectSelection(null); // Can't have objectSelection = [null]
    }

    let returnValue = vals;
    if (Array.isArray(vals)) {
        returnValue = vals.map((a) => (a[objectKey] ? a[objectKey] : a));
    }

    onChange(returnValue);
};

const getOptionLabel = ({
    disableAllOption,
    labelPreValue,
    object,
    objectIdToName,
    objectKey,
    unselectedOption,
    unselectedValue
}) => {
    if (Array.isArray(object)) {
        let response = '';
        object.forEach((item) => {
            if (item === '0') {
                if (!disableAllOption) {
                    response += `${objectIdToName['0']}, `;
                } else {
                    response += ', ';
                }
            } else {
                response += `${objectIdToName[item]}, `;
            }
        });
        response = response.substring(0, response.length - 2);
        let text = `${labelPreValue} ${response}`.trim();
        if (!labelPreValue && response === 'undefined') {
            text = '';
        }
        return text;
    }

    if (typeof object === 'object') {
        const id = object[objectKey];
        if (objectIdToName[id]) {
            if (id === unselectedValue) {
                return `${labelPreValue} ${unselectedOption}`;
            }
            return `${labelPreValue} ${objectIdToName[id]}`;
        }
        return '';
    }

    if (objectIdToName[object]) {
        return `${labelPreValue} ${objectIdToName[object]}`;
    }

    return `${labelPreValue} `;
};

const renderVisualOnly = ({
    allValue,
    objectIdToName,
    objectIdToNameVisualOnly,
    objectSelection,
    visualOnly
}) => {
    if (objectSelection?.includes('0')) {
        return allValue;
    }

    let value = '';

    /* This exists so you can send react components for rendering visuals outside of the autocompletes.
    If you send react components to autocomplete, it will fail trying to execute .toLowerCases()
    on the component, so it can be for visual only */
    if (
        visualOnly &&
        objectSelection &&
        Array.isArray(objectSelection) &&
        objectSelection.length === 1 &&
        objectIdToNameVisualOnly[objectSelection[0]]
    ) {
        return (
            <Typography variant="p" style={{ lineHeight: 1 }}>
                {objectIdToNameVisualOnly[objectSelection[0]]}
            </Typography>
        );
    }

    if (objectSelection) {
        Object.values(objectSelection).forEach((id) => {
            const opt = objectIdToName[id];

            if (opt === undefined) {
                return;
            }

            value += `${opt}, `;
        });
    }

    value = value.substring(0, value.length - 2).trim();

    return value;
};

const renderOption = ({
    disableAllOption,
    labelPreValue,
    object,
    objectIdToName,
    objectIdToNameVisualOnly,
    objectKey,
    props,
    unselectedOption,
    unselectedValue
}) => {
    const id = object[objectKey];

    if (
        Helpers.isset(objectIdToNameVisualOnly, id) &&
        objectIdToNameVisualOnly[id] !== undefined
    ) {
        const jsx = objectIdToNameVisualOnly[id];
        return (
            <Box
                {...props}
                key={id}
                mb="8px"
                style={{ fontSize: FontSizes[1] }}
            >
                {jsx}
            </Box>
        );
    }

    return (
        <Box {...props} key={id} mb="16px" style={{ fontSize: FontSizes[1] }}>
            {getOptionLabel({
                disableAllOption,
                labelPreValue,
                object,
                objectIdToName,
                objectKey,
                unselectedOption,
                unselectedValue
            })}
        </Box>
    );
};

const load = ({
    allValue,
    disableAllOption,
    multiple,
    objectKey,
    objectNameField,
    objectNameFieldVisualOnly,
    objectPayload,
    selectedValue,
    setObjectIdToName,
    setObjectIdToNameVisualOnly,
    setObjects,
    setObjectSelection,
    setOptions,
    unselectedOption,
    unselectedValue,
    value
}) => {
    let objectSelection = ['0'];

    if (!multiple) {
        objectSelection = unselectedValue;
    }

    if (selectedValue) {
        objectSelection = selectedValue;
    }

    if (value !== undefined) {
        if (typeof value === 'object') {
            if (value !== null) {
                objectSelection = value;
            } else {
                objectSelection = null; // Can't have objectSelection = [null]
            }
        } else {
            objectSelection = [value];
        }
    }

    const objectIdToName = {};
    let objects = [];
    const objectIdToNameVisualOnly = {};
    if (unselectedOption || unselectedOption !== undefined) {
        if (!multiple)
            objects.push({
                [objectKey]: unselectedValue,
                [objectNameField]: unselectedOption
            });
        if (multiple && disableAllOption) {
            objects.push({
                [objectKey]: '0',
                [objectNameField]: unselectedOption
            });
            objectIdToName['0'] = unselectedOption;
            objectIdToNameVisualOnly['0'] = unselectedOption;
        } else {
            objectIdToName[unselectedValue] = unselectedOption;
            objectIdToNameVisualOnly[unselectedValue] = unselectedOption;
        }
    }

    // !!allValue so that it does not add a 0: {id: '0', name: undefined}
    if (!disableAllOption && !!allValue) {
        objects.push({
            [objectKey]: '0',
            [objectNameField]: allValue
        });
        objectIdToName['0'] = allValue;
        objectIdToNameVisualOnly['0'] = allValue;
    }

    Object.values(objectPayload).forEach((item) => {
        objects.push(item);
        objectIdToName[item[objectKey]] = item[objectNameField];
        objectIdToNameVisualOnly[item[objectKey]] =
            item[objectNameFieldVisualOnly];
    });

    // If 'hideOption' is true, hide it from the dropdown
    objects = objects.filter((o) => !o.hideOption);

    // Filter id duplicates
    const filteredObjects = objects.reduce((unique, o) => {
        if (!unique.some((obj) => obj[objectKey] === o[objectKey])) {
            unique.push(o);
        }
        return unique;
    }, []);

    setObjectSelection(objectSelection);
    setObjects(objects);
    setObjectIdToName(objectIdToName);
    setObjectIdToNameVisualOnly(objectIdToNameVisualOnly);
    setOptions(filteredObjects);
};

const ObjectPicker = (props) => {
    const theme = useTheme();
    const {
        addTitle,
        allValue,
        disableAllOption,
        disableClearable,
        disableCloseOnSelect,
        disabled,
        freeInput,
        objectKey = 'id',
        labelPreValue = '',
        multiple,
        name,
        noFullWidth,
        objectNameField,
        objectNameFieldVisualOnly,
        objectPayload = false,
        onChange,
        onClose,
        onOpen,
        open,
        rerender,
        selectedValue,
        size,
        style,
        title,
        unselectedOption,
        unselectedValue,
        useIceberg,
        value,
        variant = 'standard',
        visualOnly
    } = props;

    const [objectSelection, setObjectSelection] = useState(['0']);
    const [objects, setObjects] = useState([]);
    const [objectIdToName, setObjectIdToName] = useState({});
    const [objectIdToNameVisualOnly, setObjectIdToNameVisualOnly] = useState(
        {}
    );
    const [options, setOptions] = useState(objects);

    useEffect(() => {
        load({
            allValue,
            disableAllOption,
            multiple,
            objectKey,
            objectNameField,
            objectNameFieldVisualOnly,
            objectPayload,
            selectedValue,
            setObjectIdToName,
            setObjectIdToNameVisualOnly,
            setObjects,
            setObjectSelection,
            setOptions,
            unselectedOption,
            unselectedValue,
            value
        });
    }, [
        allValue,
        disableAllOption,
        multiple,
        objectKey,
        objectNameField,
        objectNameFieldVisualOnly,
        objectPayload,
        rerender,
        selectedValue,
        unselectedOption,
        unselectedValue,
        value
    ]);

    if (visualOnly) {
        return (
            <>
                {renderVisualOnly({
                    allValue,
                    objectIdToName,
                    objectIdToNameVisualOnly,
                    objectSelection,
                    visualOnly
                })}
            </>
        );
    }

    // TODO: Temporary until we start using FormSelect from Iceberg over our Autocomplete
    if (useIceberg) {
        return (
            <FormSelect
                labelWidth="176"
                label={title}
                name={name}
                options={objectPayload}
                value={value}
                alignItems="center"
                color={theme.palette.text.primary}
            />
        );
    }

    return (
        <Autocomplete
            clearOnBlur={freeInput}
            data-testid="objectPickerAutocomplete"
            defaultValue={objectSelection}
            disableClearable={disableClearable || freeInput}
            disableCloseOnSelect={multiple || disableCloseOnSelect}
            disabled={disabled}
            freeSolo={freeInput}
            fullWidth={!noFullWidth}
            getOptionDisabled={(object) =>
                object[objectKey] && object[objectKey] === '0'
            }
            getOptionLabel={(object) =>
                getOptionLabel({
                    disableAllOption,
                    labelPreValue,
                    object,
                    objectIdToName,
                    objectKey,
                    unselectedOption,
                    unselectedValue
                })
            }
            handleHomeEndKeys={freeInput}
            isOptionEqualToValue={(option, val) => {
                let valueTmp = val;

                if (option[objectKey] && valueTmp?.[objectKey]) {
                    return option[objectKey] === valueTmp[objectKey];
                }

                if (!Array.isArray(valueTmp)) {
                    valueTmp = [valueTmp];
                }

                return valueTmp.indexOf(option[objectKey]) > -1;
            }}
            multiple={multiple}
            onChange={(e, v) => {
                handleValueChange({
                    event: e,
                    multiple,
                    objectKey,
                    onChange,
                    setObjectSelection,
                    value: v
                });
            }}
            onClose={(e) => onClose && onClose(e)}
            onOpen={(e) => onOpen && onOpen(e)}
            open={open}
            options={options}
            popupIcon={<ExpandMore />}
            renderOption={(renderOptionProps, object) =>
                renderOption({
                    disableAllOption,
                    labelPreValue,
                    object,
                    objectIdToName,
                    objectIdToNameVisualOnly,
                    objectKey,
                    props: renderOptionProps,
                    unselectedOption,
                    unselectedValue
                })
            }
            selectOnFocus
            size={size === 'sm' ? 'small' : 'medium'}
            style={style}
            value={objectSelection}
            renderInput={(p) => {
                const params = p;
                if (addTitle) {
                    params.label = title;
                }

                return (
                    <TextField
                        data-testid="objectPickerAutocompleteTextField"
                        {...params}
                        variant={variant}
                        color="secondary"
                        inputProps={{
                            'data-testid':
                                'objectPickerAutocompleteTextFieldInputProps',
                            ...params.inputProps,
                            autoComplete: 'new-password' // disable autocomplete and autofill
                        }}
                    />
                );
            }}
        />
    );
};

export default ObjectPicker;
